import React from 'react';
import {SidebarContent} from '../SidebarContent';
import './MeasurementSidebar.scss';
import {Col, Label, LabelValue, Layout, NoData, Row, Text, Title} from '@mc/ui-v4';
import {EditorToolType} from '@mc/react-mapio';
import {mcRules} from '@mc/react-ui-icons';

/**
 * přemapovaná struktura z angularu na měření
 * @param azimuth směr od posledního zaznamenaného bodu ve stupních
 * @param distance celková délka trasy, případně odvod celé oblasti
 * @param toolType MEASURE_LINE pro trasu, MEASURE_POLYGON pro oblast
 * @param surface celková plocha při měření oblasti
 * @param distanceItems pole jednotlivých úseček (spoj dvou bodů)
 * @param distanceItems.coordinates bod ze kterého úsečka vychází, na indexu 0 je Y a na indexu 1 je X
 * @param distanceItems.coordinates2 stejné ale jde o bod, kterým úsečka končí
 * @param distanceItems.length vzdálenost mezi dvěma body
 */
export type MeasurementInfo = {
    azimuth: number;
    distance: number;
    toolType: string;
    distanceItems: {
        coordinates: number[];
        coordinates2: number[];
        length: number;
        // geom: any;
    }[];
    surface?: number;
};
type Props = {onClose: () => void; measurementInfo: MeasurementInfo};
/**
 * na kolik desetinných míst zaokrouhlit čísla
 */
const FIXED_DIGITS = 2;

/**
 * Komponenta pro měření vzdálenosti a plochy na mapě
 * @param onClose minimalizace komponenty
 * @param measurementInfo přemapovaná data z angularu (viz. type)
 * todo po ESC vymazat data možná?
 */
export const MeasurementSidebar: React.FC<Props> = ({onClose, measurementInfo}) => {
    if (!measurementInfo && measurementInfo === null) {
        return (
            <SidebarContent
                title={measurementInfo?.toolType === EditorToolType.MEASURE_POLYGON ? 'Měření plochy' : 'Měření vzdálenosti'}
                icon={mcRules}
                onClose={onClose}
            >
                <NoData>Chybí data</NoData>
            </SidebarContent>
        );
    }
    const distanceItems = measurementInfo.distanceItems;
    // poslední bod, se kterým momentálně jezdím po mapě kurzorem
    const lastDistanceItem = distanceItems ? distanceItems[distanceItems.length - 1] : undefined;

    return (
        <SidebarContent
            title={measurementInfo.toolType === EditorToolType.MEASURE_POLYGON ? 'Měření plochy' : 'Měření vzdálenosti'}
            icon={mcRules}
            onClose={onClose}
            style={{width: '244px' /*todo mozna nejak lepe?*/}}
        >
            <div>
                <div className={'measurement-header p-1'}>
                    {measurementInfo.toolType === EditorToolType.MEASURE_LINE && (
                        <>
                            <div className={'measurement-header-upper'}>
                                <Label style={{color: 'white'}}>Vzdálenost</Label>
                                <div className={'d-flex'}>
                                    <Title intent={'inherit'}>{measurementInfo.distance?.toFixed(FIXED_DIGITS).replace('.', ',')}</Title>
                                    <Text inverse className={'ml-1 mt-auto'}>
                                        m
                                    </Text>
                                </div>
                            </div>
                            <div className={'measurement-header-lower'}>
                                <LabelValue horizontal label={'Azimut'} strong intent={'secondary'} className={'mb-0'}>
                                    {measurementInfo.azimuth?.toFixed(FIXED_DIGITS).replace('.', ',')} °
                                </LabelValue>
                            </div>
                        </>
                    )}
                    {measurementInfo.toolType === EditorToolType.MEASURE_POLYGON && (
                        <>
                            <div className={'measurement-header-upper'}>
                                <Label style={{color: 'white'}}>Plocha</Label>
                                <div className={'d-flex'}>
                                    <Title intent={'inherit'}>{measurementInfo.surface?.toFixed(FIXED_DIGITS).replace('.', ',')}</Title>
                                    <Text inverse className={'ml-1 mt-auto'}>
                                        m&sup2;
                                    </Text>
                                </div>
                            </div>
                            <div className={'measurement-header-lower'}>
                                <LabelValue horizontal label={'Obvod'} strong intent={'secondary'} className={'mb-0'}>
                                    {measurementInfo.distance?.toFixed(FIXED_DIGITS).replace('.', ',')} m
                                </LabelValue>
                                <LabelValue horizontal label={'Azimut'} strong intent={'secondary'} className={'mb-0'}>
                                    {measurementInfo.azimuth?.toFixed(FIXED_DIGITS).replace('.', ',')} °
                                </LabelValue>
                            </div>
                        </>
                    )}
                </div>
                <div className={'p-1'}>
                    <div className={'measurement-points p-1'}>
                        <Layout className={'point-content'}>
                            <Title className={'p-1 pb-2'} type={'block'}>
                                Vzdálenost bodů
                            </Title>
                            {distanceItems?.length > 0 &&
                                distanceItems?.map((distanceItem, index) => createDistancePoint(distanceItem.coordinates, distanceItem.length, index))}
                            {lastDistanceItem &&
                                createDistancePoint(
                                    lastDistanceItem.coordinates2,
                                    lastDistanceItem.length,
                                    distanceItems.length,
                                    true,
                                    measurementInfo.toolType === EditorToolType.MEASURE_POLYGON,
                                )}
                        </Layout>
                    </div>
                </div>
            </div>
        </SidebarContent>
    );
};

function createDistancePoint(coordinates: number[], length: number, index: number, last: boolean = false, isArea: boolean = false): JSX.Element {
    if (coordinates?.length !== 2) {
        console.error('Nevhodné coordinates předány:', coordinates);
        return <>---</>;
    }
    return (
        <>
            <div className={'measurement-point'}>
                <div className={'measurement-point-index'}>{last && isArea ? 1 : index + 1}</div>
                {/*minus, protože z angularu hodnoty coordinates chodí v záporných hodnotách*/}
                <div className={'measurement-point-coords'}>
                    <Text>{`Y ${-coordinates[0]?.toFixed(FIXED_DIGITS)}`}</Text>
                    <Text>{`X ${-coordinates[1]?.toFixed(FIXED_DIGITS)}`}</Text>
                </div>
            </div>
            {!last && (
                //todo separator udělat lépe než tímhle ošklivou nepěkným hackem
                <Row className={'py-2'}>
                    <Col xs={2}>
                        <div style={{padding: '2px'}}>
                            <div className={'measurement-point-distance-sep'} />
                        </div>
                    </Col>
                    <Col>
                        <div className={'measurement-point-distance'}>{`${length?.toFixed(FIXED_DIGITS)} m`}</div>
                    </Col>
                </Row>
            )}
        </>
    );
}
